import * as React from 'react';
import { Box, Typography } from '@mui/material';

/**
 * Decorative footer for the home page: a larger version of the Athena owl
 * (Tyto cyan, matching the brand) that periodically winks, with a friendly
 * "Make it a great day!" greeting below it.
 *
 * The SVG is inlined so we can target a single eye path for the wink
 * animation. `transformBox: 'fill-box'` makes the scale transform relative
 * to that path's own bounding box (otherwise SVG transforms would scale
 * around the SVG root and the eye would slide off).
 */
const OwlGreeting = () => {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 1.5,
        mt: 4,
        mb: 2,
        // Entrance: spin twice (720°) on the owl's center axis once on mount.
        '@keyframes athena-owl-spin': {
          '0%': { transform: 'rotate(0deg)' },
          '100%': { transform: 'rotate(-720deg)' },
        },
        // Wink: eyes open most of the cycle, then a brief squish near the
        // start (so the first wink lands just after the spin completes).
        '@keyframes athena-owl-wink': {
          '0%, 8%, 12%, 100%': { transform: 'scaleY(1)' },
          '10%, 11%': { transform: 'scaleY(0.08)' },
        },
        // Text fly-in: starts off-screen to the left, slides into place.
        '@keyframes athena-owl-fly-in': {
          '0%': { opacity: 0, transform: 'translateX(-40px)' },
          '100%': { opacity: 1, transform: 'translateX(0)' },
        },
        '& .athena-owl-svg': {
          transformOrigin: 'center',
          animation: 'athena-owl-spin 1.5s ease-in-out 1',
        },
        '& .athena-owl-eye-wink': {
          transformBox: 'fill-box',
          transformOrigin: 'center',
          // Delay so the wink doesn't compete with the spin.
          animation: 'athena-owl-wink 4s ease-in-out 1.5s infinite',
        },
        // Text flies in from the left, kicking off right as the first
        // wink begins (~1.9s). `both` fill-mode keeps it hidden + offset
        // before the delay and pinned in place afterward.
        '& .athena-owl-greeting-text': {
          animation: 'athena-owl-fly-in 0.6s ease-out 1.9s both',
        },
      }}
    >
      <Box
        component="svg"
        className="athena-owl-svg"
        viewBox="0 0 48 62"
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        aria-hidden="true"
        sx={{ width: 72, height: 93, color: '#0092C8' }}
      >
        {/* Top headband */}
        <path
          d="M40.274 5.51122C40.1743 5.49128 30.3118 3.37759 21.2509 3.37759C12.1899 3.37759 5.2387 5.46336 5.16692 5.4833L4.36133 2.81129C4.66043 2.71956 11.827 0.585938 21.2509 0.585938C30.6747 0.585938 40.4495 2.69164 40.8642 2.78337L40.274 5.51122Z"
          fill="currentColor"
        />
        {/* Right wing tail */}
        <path
          d="M44.3944 61.3296C44.0714 61.3296 43.7564 61.2179 43.5011 61.0065C41.7264 59.5309 25.5508 47.6664 26.1769 38.2864C26.6475 31.2315 40.1551 27.8018 41.7463 27.4349C42.3765 27.2873 43.0265 27.5984 43.3137 28.1806C43.5091 28.5794 47.3775 36.0092 47.892 44.3643C48.57 55.4073 45.8501 59.9577 45.7225 60.3565C45.5829 60.7992 45.2319 61.1421 44.7853 61.2737C44.6577 61.3136 44.5221 61.3296 44.3944 61.3296ZM41.2877 30.4219C35.3176 31.8776 29.3155 35.1319 29.3155 38.5576C29.3155 45.4091 38.4243 52.8389 43.6566 57.4571C45.3316 55.5508 45.5031 48.4521 45.2758 44.3603C45.0206 39.762 42.4602 32.9863 41.2837 30.4219H41.2877Z"
          fill="currentColor"
        />
        {/* Brow tufts */}
        <path
          d="M22.1144 26.6792C19.6059 26.6792 18.9877 23.0739 18.8721 22.1846C18.2659 19.3251 17.209 15.8037 16.543 15.0539C15.7454 14.1566 0.877843 3.19335 0.347428 2.74669C-0.182987 2.30002 -0.0434039 1.54229 0.347428 1.00789C0.802069 0.393721 1.99052 -0.0768726 2.50897 0.573185C3.37837 1.66193 17.6597 11.664 18.9837 13.2353C20.3836 14.8944 21.3048 21.714 21.3965 22.1567C21.5201 22.7629 21.8871 22.7908 22.1303 22.7988C22.3736 22.8067 22.7126 22.4358 22.8522 21.4827C23.582 16.6292 24.252 13.8774 25.6239 12.8924C25.6239 12.8924 41.4725 0.792529 42.5174 0.0587222C42.9082 -0.216455 43.5025 0.545268 43.8494 0.900207C44.4237 1.48646 44.6111 1.97699 44.2083 2.28407L27.7336 14.8027C26.7007 15.8755 26.2141 19.1895 25.4125 22.045C24.8183 24.1667 23.8612 26.6792 22.1144 26.6792Z"
          fill="currentColor"
        />
        {/* Face/body */}
        <path
          d="M20.5413 24.231H10.6628C7.24505 24.231 4.78839 22.8152 3.1453 21.3157C3.15727 25.2719 3.21311 31.1344 3.43644 34.2531C3.79935 39.3498 7.05363 46.9072 9.76552 48.9571L9.88118 49.0448C13.1195 51.4935 17.5582 54.8475 26.348 55.1027C32.8446 55.2902 37.2195 55.2144 39.2295 55.1546L41.9653 56.5624C41.9374 57.2802 41.3671 57.8505 40.6492 57.8904C40.5136 57.8984 38.3361 58.014 34.1925 58.014C32.0669 58.014 29.4228 57.9821 26.2682 57.8944C16.5891 57.6152 11.5402 53.7986 8.19821 51.2702L8.08255 51.1825C4.42947 48.4227 1.03163 39.7726 0.648773 34.4485C0.285858 29.3836 0.353647 17.7384 0.353647 17.2479C0.353647 16.5938 0.812277 16.0315 1.45037 15.8919C2.08846 15.7523 2.73454 16.0714 3.0137 16.6616C3.11739 16.873 5.37065 21.4394 10.6588 21.4394H20.5373V24.231H20.5413ZM43.066 29.934L43.5685 17.7504C43.5924 17.1641 43.2495 16.6257 42.7071 16.4024C42.1647 16.1791 41.5425 16.3187 41.1437 16.7494C39.0779 19.0066 36.0869 21.4394 34.8585 21.4394H23.8355L23.5882 24.231H34.8585C36.6492 24.231 38.8985 22.6438 40.6373 21.1363L40.3023 29.2759L43.07 29.934"
          fill="currentColor"
        />
        {/* Lower wing detail */}
        <path
          d="M12.5459 52.1805L9.88184 51.339C9.9616 51.0877 11.8878 45.1056 17.3914 40.1285C23.3496 34.7406 27.218 34.4375 28.3865 34.4375L28.1792 36.1125V37.5083C28.1472 37.5083 24.3386 37.616 19.2658 42.2023C14.3445 46.649 12.5618 52.1246 12.5459 52.1805Z"
          fill="currentColor"
        />
        {/* RIGHT eye (winks) */}
        <path
          className="athena-owl-eye-wink"
          d="M35.309 14.3405C35.0697 14.0374 34.5633 14.3046 33.8733 14.4841C32.7327 14.7872 31.0777 15.3655 30.304 16.3186C29.8454 16.8889 29.7656 18.1412 30.3558 18.6397C31.1535 19.3097 32.5094 19.1222 33.3987 18.0534C34.0887 17.2239 34.5593 16.51 34.8863 15.7244C35.0737 15.2737 35.5483 14.6396 35.309 14.3405Z"
          fill="currentColor"
        />
        {/* LEFT eye */}
        <path
          d="M9.27298 14.245C9.00578 14.5282 9.34477 14.9868 9.62394 15.6449C10.0866 16.7296 10.9001 18.285 11.957 18.9111C12.5871 19.282 13.8393 19.1823 14.2461 18.5242C14.7925 17.6389 14.4136 16.3268 13.2292 15.597C12.3079 15.0307 11.5342 14.6718 10.7127 14.4604C10.2421 14.3408 9.54417 13.9619 9.28096 14.241L9.27298 14.245Z"
          fill="currentColor"
        />
        {/* Talons */}
        <path
          d="M20.3876 59.7158C20.7984 59.7317 20.8862 59.1495 21.1613 58.4795C21.616 57.3708 22.1584 55.6599 21.8194 54.3997C21.62 53.6499 20.587 52.7327 19.7894 52.8643C18.7126 53.0397 18.0227 54.2441 18.3856 55.6759C18.6688 56.7885 18.9878 57.646 19.4544 58.4396C19.7216 58.8942 19.9729 59.7038 20.3837 59.7198L20.3876 59.7158Z"
          fill="currentColor"
        />
        <path
          d="M26.0371 60.8877C26.4478 60.9036 26.5356 60.3214 26.8107 59.6514C27.2654 58.5427 27.8078 56.8318 27.4688 55.5715C27.2694 54.8218 26.2365 53.9045 25.4388 54.0361C24.3621 54.2116 23.6721 55.416 24.035 56.8477C24.3182 57.9604 24.6372 58.8178 25.1038 59.6115C25.371 60.0661 25.6223 60.8757 26.0331 60.8916L26.0371 60.8877Z"
          fill="currentColor"
        />
        <path
          d="M31.6308 60.8877C32.0416 60.9036 32.1293 60.3214 32.4045 59.6514C32.8591 58.5427 33.4015 56.8318 33.0625 55.5715C32.8631 54.8218 31.8302 53.9045 31.0326 54.0361C29.9558 54.2116 29.2659 55.416 29.6288 56.8477C29.9119 57.9604 30.231 58.8178 30.6976 59.6115C30.9648 60.0661 31.2161 60.8757 31.6268 60.8916L31.6308 60.8877Z"
          fill="currentColor"
        />
      </Box>
      <Typography
        className="athena-owl-greeting-text"
        sx={{ color: '#1F4D83', fontWeight: 700, letterSpacing: 0.2 }}
      >
        Make it a great day!
      </Typography>
    </Box>
  );
};

export default OwlGreeting;
